import React, { useContext, useState, useCallback } from "react";
import { Divider, TextField } from "@mui/material";
import { BlobServiceClient, AnonymousCredential, ContainerClient } from "@azure/storage-blob";
import { AZURE_BLOB_URL } from "common/constants";
import { v4 as uuidv4 } from "uuid";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import { useForceIngestTableForMatcherDataMutation } from "graphql/types/graphql";
import { context } from "utils/Provider";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import colors from "assets/theme-dark/base/colors";
import pxToRem from "assets/theme/functions/pxToRem";
import { DropzoneComponent } from "../../upload/components/dropzone";

function MatcherIngest(): JSX.Element {
  const { user, setIsLoading, selectedCube } = useContext(context);
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const [isMutating, setIsMutating] = useState<boolean>(false);
  const [selectedSheetName, setSelectedSheetName] = useState<string | null>(null);
  const [viewName, setViewName] = useState<string>("");
  const [forceIngestMatcherDataTable, { loading }] = useForceIngestTableForMatcherDataMutation();

  const handleFileUpload = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setIsMutating(true);
    const uploadingSnackbarKey = enqueueSnackbar("Uploading file...", { variant: "info", persist: true });
    try {
      const newFileName = uuidv4() + "." + fileToUpload!.name.split(".").pop();
      await uploadFileToBlob(fileToUpload!, newFileName);
      try {
        await forceIngest(newFileName);
      } catch (e) {
        enqueueSnackbar(`${e instanceof Error ? e.message : "Something went wrong"}`, { variant: "error" });
        return;
      }

      enqueueSnackbar("Successful import", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("An error occurred while uploading the file", { variant: "error" });
    } finally {
      setIsLoading(false);
      setFileToUpload(null);
      setSelectedSheetName(null);
      setViewName("");
      closeSnackbar(uploadingSnackbarKey);
      setIsMutating(false);
    }
  };

  const forceIngest = async (newFileName: string) => {
    const fileNameWithSheet = selectedSheetName ? `${fileToUpload!.name}_${selectedSheetName}` : fileToUpload!.name;

    const result = await forceIngestMatcherDataTable({
      variables: {
        viewName,
        fileName: fileNameWithSheet,
        tableUuid: newFileName,
        cubeId: selectedCube.cubeId,
      },
      update: (cache) => {
        cache.evict({
          id: "ROOT_QUERY",
          fieldName: "matcherTables",
        });
      },
    });

    return result.data?.forceIngestTableForMatcherData;
  };

  const uploadFileToBlob = useCallback(
    async (file: File, newFileName: string) => {
      try {
        const azureToken = user.azure_sas_token;
        if (!azureToken) {
          throw new Error("Azure SAS token is not available");
        }
        //const blobServiceClient = new BlobServiceClient(`${AZURE_BLOB_URL}?${azureToken}`, new AnonymousCredential());
        const blobServiceClient = new BlobServiceClient(`https://snowflakeupload.blob.core.windows.net/?${azureToken}`, new AnonymousCredential());
        const containerClient: ContainerClient = blobServiceClient.getContainerClient("snowflake-import");

        const blobClient = containerClient.getBlockBlobClient(newFileName);
        const options = { blobHTTPHeaders: { blobContentType: file.type } };

        await blobClient.uploadData(file, options);
      } catch (e) {
        enqueueSnackbar(`${e instanceof Error ? e.message : "Something went wrong"}`, { variant: "error" });
      }
    },
    [user.azure_sas_token]
  );

  return (
    <MDBox
      sx={{
        marginTop: "10px",
        borderRadius: "8px",
        boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.2), 0px 4px 8px rgba(0, 0, 0, 0.1)",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        padding: "1rem",
        backgroundColor: colors.background.default,
      }}
    >
      <Divider sx={{ my: pxToRem(11) }} />
      <MDBox sx={{ display: "flex", justifyContent: "space-between" }}>
        <MDBox sx={{ flex: 2, marginRight: "1rem", overflow: "auto" }}>
          <MDTypography variant="h4" sx={{ marginBottom: "0.5rem" }}>
            Ingest Matcher Data
          </MDTypography>
          <form onSubmit={handleFileUpload}>
            <MDBox component="div" sx={{ display: "flex", flexDirection: "column", gap: 1, width: "30%" }}>
              <TextField
                label="View Name"
                value={viewName}
                onChange={(e) => setViewName(e.target.value)}
                inputProps={{
                  style: { color: colors.primary.main },
                }}
                required
                size="small"
                disabled={isMutating}
                helperText={viewName ? "" : "Please enter a view name"}
                sx={{ marginBottom: "1rem" }}
              />
            </MDBox>
            <DropzoneComponent
              buttonDisabled={isMutating}
              isMutating={isMutating}
              setIsMutating={setIsMutating}
              fileToUpload={fileToUpload}
              setFileToUpload={setFileToUpload}
              setSelectedSheetName={setSelectedSheetName}
              mutationParam={"matcher"}
              dropzoneWidth={"100%"}
            />
            <MDButton
              type="submit"
              variant="background"
              disabled={isMutating || !fileToUpload || !viewName.trim()}
              sx={{
                width: "fit-content",
                marginTop: "1rem",
              }}
            >
              Import and ingest
            </MDButton>
          </form>
        </MDBox>
      </MDBox>
      <Divider sx={{ my: 2 }} />
    </MDBox>
  );
}

export default MatcherIngest;
